import {
  FunctionComponent,
  useRef,
  useCallback,
  useEffect,
  useState,
} from 'react'
import { Button, Link, toast } from '@conexasaude/hero'
import ReCAPTCHA from 'react-google-recaptcha'
import { useHistory, useParams, useLocation } from 'react-router'
import { Location } from 'history'
import { Form } from '@unform/web'
import { ZendeskAPI } from 'react-zendesk'
import loginStyles from '@/modules/Auth/pages/Login/login-styles'
import Icon from '@/components/HeroIcons'
import { useAuthContext } from '@/modules/Auth/contexts/AuthContext'
import services from '../../auth-services'
import { IUser } from '@/modules/Auth/auth-types'
import Input from '@/components/Form/Input'
import { getThemeByUniqueCode } from '@/theme'
import { useThemeGeneratorContext } from '@/theme/contexts/ThemeGeneratorContext'
import StartForm from '@/modules/Auth/components/StartForm'
import { initializeBrowserNotification } from '@/components/BrowserNotification'
import { setCache, getCache } from '@/utils/cache'
// import { useModalContext } from '@/components/Modal/contexts/ModalContext'
// import { NewAccessModal } from '../../components/NewAccessModal'
// import { getUserAgent } from '@/utils/dom'
import AuthLayout from '@/components/AuthLayout'
import { useChatLayerStore } from '@/components/ChatLayer'
import { RenderIf } from '@/components/RenderIf'

type LoginLocation = {
  from: Location
}

const Login: FunctionComponent = () => {
  const params = useParams<{ clinic?: string }>()
  const location = useLocation<LoginLocation>()
  const { push } = useHistory()
  const [loading, setLoading] = useState(false)
  const [inputError, setInputError] = useState(false)
  const [form, setForm] = useState('start-form')
  const [imageError, setImageError] = useState(false)
  const recaptchaRef = useRef<ReCAPTCHA>(null)
  const formRef = useRef()
  const { theme, setThemeColors } = useThemeGeneratorContext()
  const { signIn } = useAuthContext()
  // const { showModal } = useModalContext()
  const { container } = loginStyles()
  const { openChatLayer } = useChatLayerStore()

  const redirectIfAuthenticated = (pathname?: string) => {
    if (location?.state?.from) {
      push(location?.state?.from?.pathname)
      return
    }

    push(pathname)
  }

  useEffect(() => {
    if (getCache('user')) {
      redirectIfAuthenticated('/')
      return
    }

    if (params.clinic) {
      getThemeByUniqueCode(params.clinic, theme)
        .then(res => setThemeColors(res))
        .catch(() => setThemeColors(null))
      setCache('unique-code', params.clinic)
    } else {
      setThemeColors(null)
      setCache('unique-code', '')
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])

  const login = useCallback(
    async ({ user, pass }) => {
      try {
        setInputError(false)
        setLoading(true)
        const token = await recaptchaRef.current.executeAsync()
        const users: IUser[] = await services.login(user, pass, token, true)

        signIn(users)
        setLoading(false)
        recaptchaRef.current.reset()
        initializeBrowserNotification()
        if (params.clinic)
          push(`/escolha/${params.clinic}`, {
            from: location?.state?.from,
          })
        else
          push(`/escolha`, {
            from: location?.state?.from,
          })
      } catch (e) {
        recaptchaRef.current.reset()
        setInputError(true)
        setLoading(false)

        if (e?.response?.data?.msg === 'STATUS_USUARIO_INATIVO') {
          toast(
            'Notamos que você não ativou sua conta. Para realizar ou agendar uma consulta, ative sua conta agora.',
            {
              action: redirectToActivation,
              actionLabel: 'Ativar conta',
              theme: 'dark',
              variant: 'warning',
              autoClose: 10000,
            },
          )

          return
        }

        if (e?.response?.data?.msg === 'STATUS_USUARIO_INATIVO_DEPENDENTE') {
          toast(
            'Para continuar, você precisa ativar sua conta ou a dos seus dependentes.',
            {
              ...(window.innerWidth >= 639 && {
                action: redirectToActivation,
                actionLabel: 'Ativar conta',
              }),
              theme: 'dark',
              variant: 'warning',
              autoClose: 10000,
            },
          )

          return
        }

        if (e?.response?.data?.status === 401) {
          const errorMessage =
            e?.response?.data?.msg || e?.response?.msg || e.message

          toast(errorMessage, {
            theme: 'dark',
            variant: 'danger',
            autoClose: 10000,
          })
          return
        }

        if (e?.response?.data?.status === 422) {
          const errorMessage =
            e?.response?.data?.msg || e?.response?.msg || e.message

          if (
            e?.response?.data?.msg ===
            'Não encontramos nenhuma conta associada a este e-mail ou CPF. Por favor, tente novamente ou fale com Suporte.'
          ) {
            const toastMessageId = toast(
              errorMessage,

              {
                ...(window.innerWidth >= 639 && {
                  action: () => {
                    ZendeskAPI('webWidget', 'show')
                    ZendeskAPI('webWidget', 'open')
                    toast.dismiss(toastMessageId)
                  },
                  actionLabel: 'Falar com Suporte',
                }),
                theme: 'dark',
                variant: 'warning',
                autoClose: false,
              },
            )
          } else {
            toast(errorMessage, {
              theme: 'dark',
              variant: 'danger',
              autoClose: 10000,
            })
          }
          return
        }

        const errorMessage =
          e?.response?.data?.msg || e?.response?.msg || e.message

        toast(errorMessage, {
          theme: 'dark',
          variant: 'danger',
          autoClose: 10000,
        })
      }
    },
    // eslint-disable-next-line
    [params.clinic, push, signIn],
  )

  const goToForgotPassword = useCallback(() => {
    if (params.clinic) {
      push(`/esqueci-senha/${params.clinic}`)

      return
    }

    push('/esqueci-senha')
  }, [params.clinic, push])

  const goToCreateAccount = useCallback(() => {
    push('/cadastro/inicio')
  }, [push])

  const redirectToActivation = useCallback(() => {
    let activationUrl = process.env.REACT_APP_ACTIVATION_PAGE_URL
    if (params?.clinic) {
      activationUrl += `/${params?.clinic}`
    }
    window.open(activationUrl, '_self')
  }, [params?.clinic])

  return (
    <AuthLayout>
      <div css={container}>
        {form && form === 'start-form' ? (
          <StartForm onClickLogin={() => setForm('login-form')} />
        ) : (
          <div className="login-form">
            <RenderIf condition={!!params.clinic && !imageError}>
              <img
                src={`${process.env.REACT_APP_LINK_LOGO_AWS}${params.clinic}.png`}
                alt="logo"
                className={'logo-whitelabel mb-3'}
                onError={event => {
                  event.currentTarget.onerror = null
                  setImageError(true)
                  event.currentTarget.style.height = 'auto'
                }}
              />
            </RenderIf>
            <RenderIf condition={!params.clinic || imageError}>
              <Icon
                name="conexa-logo"
                width={'270px'}
                height={'60px'}
                className="mb-3"
              />
            </RenderIf>

            <h3>Utilize o seu e-mail ou CPF e senha cadastrada para entrar.</h3>
            <Form
              autoComplete="off"
              className="input-form"
              ref={formRef}
              onSubmit={data => login(data)}
            >
              <Input
                error={inputError}
                label="E-mail ou CPF"
                inputProps={{ name: 'user' }}
                placeholder="Digite seu e-mail ou numero do seu CPF"
                fullWidth
              />
              <Input
                error={inputError}
                label="Senha"
                inputProps={{ name: 'pass', type: 'password' }}
                placeholder="Digite a sua senha"
                fullWidth
              />
              <br />

              <Button
                size="md"
                busy={loading}
                fullWidth
                id="Login-AcessarConta-CTA_Entrar"
              >
                Entrar
              </Button>

              <div className="link-login">
                <Link onClick={goToForgotPassword}>Esqueceu sua senha?</Link>
                <span className="divider">|</span>
                <Link onClick={goToCreateAccount}>Crie sua conta</Link>
              </div>

              <Link className="link-support" onClick={openChatLayer}>
                Precisa de ajuda?
              </Link>

              <ReCAPTCHA
                ref={recaptchaRef}
                size="invisible"
                sitekey={process.env.REACT_APP_RECAPTCHA_SITE_KEY}
              />
            </Form>
          </div>
        )}
      </div>
    </AuthLayout>
  )
}

export default Login
